// RESUME SET STATUS **********************************************************************************
// pathname: /v2/resume-operation/${userSlug}/status/${status}
// ****************************************************************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi';
import { SwrToastsProps } from '@type/swr';

// Shape returned by the backend on HTTP 402 when the target status
// (Shortlist / Interview) requires the recruiter to unlock the applicant
// first — see AppBundle\Model\OpenApi\ResumeData\StatusGate.
export interface StatusGatePayload {
  requiresUnlock: boolean;
  unlockPrice: number;
  currency: string;
}

/**
 * Type-guard: does this unknown value look like a StatusGate (raw 402 body
 * from the backend)? Detects the gate case reliably even though HTTP status
 * codes aren't propagated through the SWR fetcher pipeline — we key on
 * `requiresUnlock === true` which is unique to that response.
 */
export const isStatusGatePayload = (value: unknown): value is StatusGatePayload => (
  !!value
  && typeof value === 'object'
  && (value as { requiresUnlock?: unknown }).requiresUnlock === true
  && typeof (value as { unlockPrice?: unknown }).unlockPrice === 'number'
);

// Interface
// ********************************************
interface PayloadData extends ApiSchema<'OperationJob2'> {
  jobSlug?: string;
}
interface SwrResumeSetStatusProps {
  userSlug: string;
  status: string | number;
  locale: string,
  successCallback?: (response?: ApiSchema<'OperationJob'>) => void,
  errorCallback?: (errors?: ApiSchema<'ErrorResponse'>) => void,
  /**
   * Called when the backend refuses the status change with a 402
   * status-gate response. Receives the parsed gate payload so the caller
   * can open the confirm-unlock modal / roll back optimistic UI.
   * When set, the generic error toast is suppressed for gate responses so
   * the caller can show gate-specific UX instead.
   */
  onGateBlocked?: (gate: StatusGatePayload) => void,
  toastsOptions?: SwrToastsProps,
  trackingId?: string,
}

/**
 * @description SWR mutation: Set applicant status.
 * ** Path params **
 * - `userSlug`: The user slug.
 * - `status`: The desired status.
 *
 * ** Props **
 * - `locale`: The locale to use for the request.
 * - `successCallback`: The callback function to call on success.
 * - `errorCallback`: The callback function to call on error.
 * - `toastsOptions`: The SWR toasts options.
 * - `trackingId`: The tracking id for the event.
 */
export const useSwrResumeSetStatus = (props: SwrResumeSetStatusProps) => {
  // Props destructuring
  // ********************************************
  const {
    userSlug,
    status,
    locale,
    successCallback,
    errorCallback,
    onGateBlocked,
    toastsOptions,
    trackingId,
  } = props;

  // SWR mutation
  // ********************************************
  const {
    data, error, isMutating, reset, trigger,
  } = useSwrMutationApi<ApiSchema<'OperationJob'>, PayloadData>({
    url: `/v2/resume-operation/${userSlug}/status/${status}`,
    urlParams: trackingId ? `?rid=${trackingId}` : '',
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
    },
    apiOptions: {
      onSuccess: (response) => {
        // Callback
        if (successCallback) successCallback(response);
      },
      onError: (errors) => {
        // The backend surfaces the status-gate as a 402 whose body is the
        // StatusGate DTO. Route it to the gate callback and let the caller
        // show the confirm-unlock UX; the generic error toast is suppressed
        // for this case (see toastsOptions below).
        if (onGateBlocked && isStatusGatePayload(errors)) {
          onGateBlocked(errors);
          return;
        }
        // Callback
        if (errorCallback) errorCallback(errors);
      },
    },
    toastsOptions: {
      showSuccessToast: true,
      // Callers that opt into gate handling via {@link onGateBlocked} take
      // over the error UX end-to-end (they'll render a modal, inline hint,
      // etc.) so the generic error toast is suppressed for them. Callers
      // without a gate handler keep the default behavior.
      showErrorToast: !onGateBlocked,
      ...toastsOptions,
    },
  });

  // Return data
  // ********************************************
  return {
    data,
    error,
    isMutating,
    reset,
    trigger,
  };
};
